import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "notistack";
import moment from "moment";
import CustomMUITable from "src/components/CustomMUITable";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import Label from "src/components/Label";
import AddOrEditTagDrawer from "./AddOrEditTagDrawer";
import {
  deleteTrackerTagApi,
  getTrackerTagsApi,
} from "../../../DAL/trackerTag/trackerTag";

const MUTED = "#64748b";
const BORDER = "#e2e8f0";
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
  },
};

export default function TrackerTagsList() {
  const { enqueueSnackbar } = useSnackbar();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editTag, setEditTag] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteDoc, setDeleteDoc] = useState(null);

  const loadTags = async () => {
    setLoading(true);
    try {
      const res = await getTrackerTagsApi({
        limit: 500,
        status: "all",
        search: search.trim(),
      });
      if (res?.code === 200) {
        setTags(Array.isArray(res.tags) ? res.tags : []);
      } else {
        setTags([]);
        enqueueSnackbar(res?.message || "Failed to load tags", {
          variant: "error",
        });
      }
    } catch (_e) {
      setTags([]);
      enqueueSnackbar("Failed to load tags", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter((tag) =>
      `${tag.name || ""} ${tag.description || ""} ${tag.status || ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [tags, search]);

  const handleDelete = async () => {
    const id = deleteDoc?._id || deleteDoc?.id;
    if (!id) return;
    setOpenDelete(false);
    try {
      const res = await deleteTrackerTagApi(id);
      if (res?.code === 200) {
        enqueueSnackbar(res.message || "Tag deleted", { variant: "success" });
        setTags((prev) =>
          prev.filter((item) => String(item._id || item.id) !== String(id))
        );
      } else {
        enqueueSnackbar(res?.message || "Could not delete tag", {
          variant: "error",
        });
      }
    } catch (_e) {
      enqueueSnackbar("Could not delete tag", { variant: "error" });
    }
  };

  const MENU_OPTIONS = [
    {
      label: "Edit",
      icon: "akar-icons:edit",
      handleClick: (row) => {
        setEditTag(row);
        setOpenForm(true);
      },
    },
    {
      label: "Delete",
      icon: "ant-design:delete-twotone",
      handleClick: (row) => {
        setDeleteDoc(row);
        setOpenDelete(true);
      },
    },
  ];

  const TABLE_HEAD = [
    {
      id: "action",
      label: "Action",
      type: "action",
      MENU_OPTIONS: "MENU_OPTIONS",
    },
    { id: "number", label: "#", type: "number" },
    {
      id: "name",
      label: "Tag",
      renderData: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              bgcolor: row.color || "#04abbb",
              border: `1px solid ${BORDER}`,
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
            {row.name}
          </Typography>
        </Box>
      ),
    },
    {
      id: "description",
      label: "Description",
      renderData: (row) => (
        <Typography sx={{ fontSize: 13, color: MUTED }}>
          {row.description || "—"}
        </Typography>
      ),
    },
    {
      id: "status",
      label: "Status",
      renderData: (row) => (
        <Label
          variant="ghost"
          color={row.status === "active" ? "success" : "default"}
        >
          {row.status === "active" ? "Active" : "Inactive"}
        </Label>
      ),
    },
    {
      id: "updated",
      label: "Updated",
      sortValue: (row) => row.updatedAt || "",
      renderData: (row) => (
        <Typography sx={{ fontSize: 12.5, color: MUTED }}>
          {row.updatedAt ? moment(row.updatedAt).format("DD/MM/YYYY HH:mm") : "—"}
        </Typography>
      ),
    },
  ];

  const tableRows = filtered.map((row) => ({
    ...row,
    id: row._id || row.id,
    MENU_OPTIONS,
  }));

  return (
    <>
      <CustomConfirmation
        openDelete={openDelete}
        setOpenDelete={setOpenDelete}
        title="Delete this tag?"
        handleAgree={handleDelete}
      />

      <AddOrEditTagDrawer
        open={openForm}
        tag={editTag}
        onClose={() => {
          setOpenForm(false);
          setEditTag(null);
        }}
        onSaved={() => loadTags()}
      />

      <div className="container-fluid tracker-module-page">
        <div className="row mb-3">
          <div className="col-lg-6 col-sm-12">
            <h2 style={{ marginBottom: 4 }}>Tags</h2>
            <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
              Shared tag library for all trackers. Create tags here, then select
              them on each journey.
            </Typography>
          </div>
          <div className="col-lg-6 col-sm-12">
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <TextField
                size="small"
                placeholder="Search tags…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ ...fieldSx, width: { xs: "100%", sm: 240 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
                    </InputAdornment>
                  ),
                }}
              />
              <button
                type="button"
                className="small-contained-button"
                onClick={() => {
                  setEditTag(null);
                  setOpenForm(true);
                }}
              >
                New Tag
              </button>
            </Box>
          </div>
        </div>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={36} />
          </Box>
        ) : (
          <CustomMUITable
            TABLE_HEAD={TABLE_HEAD}
            data={tableRows}
            MENU_OPTIONS={MENU_OPTIONS}
            hide_search
            sortable
            onRowClick={(row) => {
              setEditTag(row);
              setOpenForm(true);
            }}
            className="card-with-background tracker-module-table"
          />
        )}
      </div>
    </>
  );
}
